import {
  uploadFile,
  type EvenementAtelier,
  type EvenementAudience,
  type EvenementInscriptionCreate,
  type EvenementMasterclass,
  type EvenementPack,
  type PaymentPreference,
} from '@mtc/shared';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AfficheThumbnail } from '../components/AfficheThumbnail';
import { Field } from '../components/Field';
import { StateMessage } from '../components/StateMessage';
import { useEvenement, useSubmitEvenementInscription } from '../data/queries';
import { useSEO } from '../hooks/useSEO';
import { useAuth } from '../lib/auth';

/** Atelier or masterclass, tagged by kind — the à-la-carte selectable items. */
type SessionItem = (EvenementAtelier | EvenementMasterclass) & {
  kind: 'atelier' | 'masterclass';
};

/** Two sessions conflict when their scheduled time ranges overlap [start,end). */
function itemsOverlap(a: SessionItem, b: SessionItem): boolean {
  const aS = Date.parse(a.startDateTime ?? '');
  const aE = Date.parse(a.endDateTime ?? '');
  const bS = Date.parse(b.startDateTime ?? '');
  const bE = Date.parse(b.endDateTime ?? '');
  if ([aS, aE, bS, bE].some((n) => Number.isNaN(n))) return false;
  return aS < bE && bS < aE;
}

function sessionTimeLabel(item: SessionItem): string {
  const s = new Date(item.startDateTime);
  const e = new Date(item.endDateTime);
  if (Number.isNaN(s.getTime())) return '';
  const t = (d: Date) => d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const day = s.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  return Number.isNaN(e.getTime()) ? `${day} · ${t(s)}` : `${day} · ${t(s)}–${t(e)}`;
}

type FormState = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  cabinet: string;
  paymentPreference: PaymentPreference;
};

const PAYMENT_OPTIONS: Array<{ value: PaymentPreference; label: string; hint: string }> = [
  { value: 'virement', label: 'Virement bancaire', hint: 'RIB envoyé après confirmation.' },
  { value: 'cheque', label: 'Chèque', hint: 'À remettre avant l\'événement.' },
  { value: 'espece', label: 'Espèces', hint: 'À régler au centre.' },
];

const AUDIENCE_OPTIONS: Array<{ value: EvenementAudience; label: string; hint: string }> = [
  { value: 'etudiant', label: 'Étudiant·e', hint: 'En cours de formation initiale.' },
  { value: 'jeune-diplome', label: 'Jeune diplômé·e', hint: '< 3 ans depuis le diplôme.' },
  { value: 'praticien', label: 'Praticien·ne', hint: 'En exercice depuis 3 ans+.' },
];

function fmtMad(n: number): string {
  return `${Math.round(n).toLocaleString('fr-FR')} MAD`;
}

/** Set equality on two string arrays (order-insensitive). */
function setEquals(a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean {
  if (a.length !== b.length) return false;
  const sb = new Set(b);
  return a.every((x) => sb.has(x));
}

export function EvenementInscription() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const eventQ = useEvenement(id);
  const { user, member, isVerified } = useAuth();
  const submit = useSubmitEvenementInscription();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [editingIdentity, setEditingIdentity] = useState(false);

  // Selections carried from EvenementDetail (mutable here).
  const selectionState = (location.state ?? {}) as {
    generalIncluded?: boolean;
    atelierIds?: string[];
    masterclassIds?: string[];
    totalAmount?: number;
  };
  const [generalIncluded, setGeneralIncluded] = useState(
    selectionState.generalIncluded ?? true,
  );
  const [atelierIds, setAtelierIds] = useState<string[]>(selectionState.atelierIds ?? []);
  const [masterclassIds, setMasterclassIds] = useState<string[]>(
    selectionState.masterclassIds ?? [],
  );
  const [packId, setPackId] = useState<string | null>(null);
  const [audience, setAudience] = useState<EvenementAudience | null>(null);
  const [audienceProofUrl, setAudienceProofUrl] = useState<string | null>(null);

  const initialState = useMemo<FormState>(() => {
    const p = member?.profile;
    return {
      firstName: p?.firstName ?? '',
      lastName: p?.lastName ?? '',
      email: user?.email ?? '',
      phone: p?.phone ?? '',
      cabinet: p?.cabinet ?? '',
      paymentPreference: 'virement',
    };
  }, [member, user]);

  const [form, setForm] = useState<FormState>(initialState);
  useEffect(() => {
    setForm(initialState);
  }, [initialState]);

  useSEO({
    title: eventQ.data ? `Inscription — ${eventQ.data.title}` : 'Inscription événement — MTC',
    description: eventQ.data?.shortDescription ?? '',
  });

  if (eventQ.isLoading) return <StateMessage variant="loading" />;
  if (eventQ.error) return <StateMessage variant="error" message={String(eventQ.error)} />;
  const event = eventQ.data;
  if (!event) return <StateMessage variant="empty" message="Événement introuvable." />;
  if (event.registrationMode !== 'hosted-mtc' || !event.hostedRegistration) {
    return (
      <StateMessage
        variant="empty"
        message="Cet événement n'utilise pas l'inscription via MTC."
      />
    );
  }

  const hosted = event.hostedRegistration;
  const ateliers = hosted.ateliers ?? [];
  const masterclasses = hosted.masterclasses ?? [];
  const packs = hosted.packs ?? [];
  const general = hosted.generalInscription;
  const conferenciers = hosted.conferenciers ?? [];
  const conferencierName = (cid: string) => conferenciers.find((c) => c.id === cid)?.name ?? '';

  // À-la-carte sessions: ateliers + masterclasses, grouped by time slot for
  // display; exclusivity is driven by time overlap (see toggleSession).
  const sessions: SessionItem[] = [
    ...ateliers.map((a) => ({ ...a, kind: 'atelier' as const })),
    ...masterclasses.map((m) => ({ ...m, kind: 'masterclass' as const })),
  ];
  const grouped = new Map<string | null, SessionItem[]>();
  for (const s of sessions) {
    const k = s.timeSlotId || null;
    const arr = grouped.get(k) ?? [];
    arr.push(s);
    grouped.set(k, arr);
  }
  for (const arr of grouped.values()) {
    arr.sort((a, b) => (a.startDateTime ?? '').localeCompare(b.startDateTime ?? ''));
  }
  const timeSlots = hosted.timeSlots ?? [];

  const generalRequired = general.required ?? true;
  const generalSoldOut = general.seats > 0 && general.seatsBooked >= general.seats;
  const canRegisterItems = !generalRequired || generalIncluded;
  const selectedSet = new Set<string>([...atelierIds, ...masterclassIds]);

  const overlapsSelection = (item: SessionItem): boolean => {
    if (selectedSet.has(item.id)) return false;
    for (const sid of selectedSet) {
      const s = sessions.find((x) => x.id === sid);
      if (s && itemsOverlap(item, s)) return true;
    }
    return false;
  };

  const toggleGeneral = (v: boolean) => {
    setGeneralIncluded(v);
    setPackId(null);
  };

  const toggleSession = (item: SessionItem) => {
    const inAtel = atelierIds.includes(item.id);
    const inMc = masterclassIds.includes(item.id);
    if (inAtel || inMc) {
      if (inAtel) setAtelierIds(atelierIds.filter((x) => x !== item.id));
      if (inMc) setMasterclassIds(masterclassIds.filter((x) => x !== item.id));
      setPackId(null);
      return;
    }
    const keep = (ids: string[]) =>
      ids.filter((id) => {
        const s = sessions.find((x) => x.id === id);
        return !(s && itemsOverlap(item, s));
      });
    const nextAtel = keep(atelierIds);
    const nextMc = keep(masterclassIds);
    if (item.kind === 'atelier') {
      setAtelierIds([...nextAtel, item.id]);
      setMasterclassIds(nextMc);
    } else {
      setAtelierIds(nextAtel);
      setMasterclassIds([...nextMc, item.id]);
    }
    setPackId(null);
  };

  const selectedPack = packId ? packs.find((p) => p.id === packId) ?? null : null;
  const availablePacks = packs.filter(
    (p) => p.audience === 'all' || (audience !== null && p.audience === audience),
  );

  // Effective pack price after any audience-based discount (e.g. -10% for
  // students / young graduates), rounded to the MAD.
  const packPrice = (p: EvenementPack): number => {
    const base = p.price ?? 0;
    const pct = p.discountPercent ?? 0;
    if (pct > 0 && audience && (p.discountAudiences ?? []).includes(audience)) {
      return Math.round(base * (1 - pct / 100));
    }
    return base;
  };

  // Pricing: auto-sum is the price WITHOUT any pack applied. Plain computation
  // (NOT useMemo) — this runs after the early returns above, so a hook here
  // would violate the rules of hooks (React error #310).
  const autoSum = (() => {
    let sum = 0;
    if (generalIncluded) sum += general.price ?? 0;
    sum += ateliers
      .filter((a) => atelierIds.includes(a.id))
      .reduce((s, a) => s + (a.price ?? 0), 0);
    sum += masterclasses
      .filter((m) => masterclassIds.includes(m.id))
      .reduce((s, m) => s + (m.price ?? 0), 0);
    return sum;
  })();

  // With a pack: pack price replaces what the pack covers; extras add at full price.
  const total = (() => {
    if (!selectedPack) return autoSum;
    const packGeneral = selectedPack.includes.general;
    const packAtelierIds = new Set(selectedPack.includes.atelierIds);
    const packMasterclassIds = new Set(selectedPack.includes.masterclassIds);
    const extraGeneralPrice =
      generalIncluded && !packGeneral ? general.price ?? 0 : 0;
    const extraAteliers = ateliers
      .filter((a) => atelierIds.includes(a.id) && !packAtelierIds.has(a.id))
      .reduce((s, a) => s + (a.price ?? 0), 0);
    const extraMasterclasses = masterclasses
      .filter((m) => masterclassIds.includes(m.id) && !packMasterclassIds.has(m.id))
      .reduce((s, m) => s + (m.price ?? 0), 0);
    return (
      packPrice(selectedPack) + extraGeneralPrice + extraAteliers + extraMasterclasses
    );
  })();

  // "Cette combinaison correspond au Pack X" hint — match current selection
  // EXACTLY against a pack the user is eligible for. Show only if pack price
  // < autoSum (otherwise no real saving, hint = noise).
  const matchingPack: EvenementPack | null = (() => {
    if (selectedPack) return null;
    for (const p of availablePacks) {
      if (
        p.includes.general === generalIncluded &&
        setEquals(p.includes.atelierIds, atelierIds) &&
        setEquals(p.includes.masterclassIds, masterclassIds) &&
        packPrice(p) < autoSum
      ) {
        return p;
      }
    }
    return null;
  })();

  const applyPack = (p: EvenementPack) => {
    setGeneralIncluded(p.includes.general);
    setAtelierIds([...p.includes.atelierIds]);
    setMasterclassIds([...p.includes.masterclassIds]);
    setPackId(p.id);
  };
  const clearPack = () => {
    setPackId(null);
  };

  const selectedAtelierObjs = ateliers.filter((a) => atelierIds.includes(a.id));
  const selectedMasterclassObjs = masterclasses.filter((m) => masterclassIds.includes(m.id));

  const memberProfileComplete = Boolean(
    member?.profile?.firstName &&
      member?.profile?.lastName &&
      user?.email &&
      member?.profile?.phone,
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    if (!audience) {
      setErrorMsg('Sélectionnez votre statut (étudiant, jeune diplômé ou praticien).');
      return;
    }
    if (!audienceProofUrl) {
      setErrorMsg('Téléversez un justificatif de votre statut (carte étudiant, diplôme ou carte professionnelle).');
      return;
    }
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim() || !form.phone.trim()) {
      setErrorMsg('Merci de compléter prénom, nom, email et téléphone.');
      return;
    }
    if (!generalIncluded && atelierIds.length === 0 && masterclassIds.length === 0) {
      setErrorMsg('Sélectionnez au moins une option (générale, atelier ou masterclass).');
      return;
    }
    if (generalRequired && !generalIncluded && (atelierIds.length > 0 || masterclassIds.length > 0)) {
      setErrorMsg("L'inscription générale est requise pour participer aux ateliers / masterclasses.");
      return;
    }

    const input: EvenementInscriptionCreate = {
      evenementId: event.id,
      generalIncluded,
      atelierIds,
      masterclassIds,
      packId,
      audience,
      audienceProofUrl,
      totalAmount: total,
      paymentPreference: form.paymentPreference,
      profile: {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        ...(form.cabinet.trim() ? { cabinet: form.cabinet.trim() } : {}),
      },
    };

    try {
      const memberUid = isVerified && user ? user.uid : null;
      await submit.mutateAsync({ ...input, memberUid });
      if (memberUid) {
        navigate('/mon-espace', { state: { eventInscriptionConfirmed: event.title } });
      } else {
        alert(
          `Inscription enregistrée ! Vous recevrez un email de confirmation à ${form.email}.`,
        );
        navigate(`/evenements/${event.id}`);
      }
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="ev-ins-page">
      <button
        type="button"
        className="ev-ins-back"
        onClick={() => navigate(`/evenements/${event.id}`)}
      >
        ← Retour à l'événement
      </button>

      <div className="ev-ins-banner">
        {event.image && (
          <div
            className="ev-ins-banner-image"
            style={{ backgroundImage: `url(${event.image})` }}
            aria-hidden
          />
        )}
        <div className="ev-ins-banner-body">
          <div className="ev-ins-banner-eyebrow">
            ★ Inscription · Gestion technique MTC
            {event.partnerName ? ` · ${event.partnerName}` : ''}
          </div>
          <h1 className="ev-ins-banner-title">
            Finaliser votre inscription à « {event.title} »
          </h1>
          <div className="ev-ins-banner-meta">
            <span>
              {new Date(event.startDate).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </span>
            {event.city && <span>{event.city}</span>}
          </div>
        </div>
      </div>

      <div className="ev-ins-grid">
        {/* ── Main column ──────────────────────────────────────────────── */}
        <form onSubmit={onSubmit}>
          {/* 1 — Statut (audience) + justificatif */}
          <div className="ev-ins-section">
            <h2 className="ev-ins-section-title">1 · Votre statut</h2>
            <div className="ins-pay-grid">
              {AUDIENCE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`ins-pay-card ${audience === opt.value ? 'is-on' : ''}`}
                >
                  <input
                    type="radio"
                    name="audience"
                    checked={audience === opt.value}
                    onChange={() => setAudience(opt.value)}
                  />
                  <strong>{opt.label}</strong>
                  <span>{opt.hint}</span>
                </label>
              ))}
            </div>
            {!audience && (
              <p style={{ fontSize: 13, color: 'rgba(15,25,22,0.6)', marginTop: 14, fontStyle: 'italic' }}>
                Sélectionnez votre statut pour afficher les formules et finaliser votre inscription.
              </p>
            )}
          </div>

          {audience && (
            <>
              {/* 2 — Votre formule : générale, puis packs valorisés, puis à la carte */}
              <div className="ev-ins-section">
                <h2 className="ev-ins-section-title">2 · Votre formule</h2>

                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px', border: `1.5px solid ${generalIncluded ? 'var(--mtc-primary, #0B5D3B)' : 'rgba(15,25,22,0.12)'}`, borderRadius: 12, background: generalIncluded ? 'rgba(11,93,59,0.04)' : 'white', cursor: generalSoldOut ? 'not-allowed' : 'pointer', opacity: generalSoldOut ? 0.5 : 1 }}>
                  <input type="checkbox" checked={generalIncluded} disabled={generalSoldOut || generalRequired} onChange={(e) => toggleGeneral(e.target.checked)} style={{ marginTop: 4 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: '#0F1916' }}>Inscription générale{generalRequired ? ' · requise' : ''}</div>
                    <div style={{ fontSize: 12.5, color: 'rgba(15,25,22,0.6)', marginTop: 2 }}>Accès aux conférences{generalSoldOut ? ' · Complet' : ''}</div>
                  </div>
                  <div style={{ fontFamily: 'var(--mtc-font-display)', fontSize: 16, color: 'var(--mtc-primary, #0B5D3B)', whiteSpace: 'nowrap' }}>{general.price > 0 ? fmtMad(general.price) : 'Gratuit'}</div>
                </label>

                {availablePacks.length > 0 && (
                  <div style={{ marginTop: 20, padding: '16px 18px 6px', border: '1px solid rgba(201,169,97,0.45)', borderRadius: 14, background: 'rgba(201,169,97,0.06)' }}>
                    <div style={{ fontFamily: 'var(--mtc-font-mono)', fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#9E7508', marginBottom: 4 }}>
                      Formules avantageuses
                    </div>
                    <div style={{ fontFamily: 'var(--mtc-font-display)', fontSize: 18, color: '#0F1916', marginBottom: 14, lineHeight: 1.2 }}>
                      Le meilleur prix, en un seul choix
                    </div>
                    <div className="ev-ins-packs-grid">
                      {availablePacks.map((p) => (
                        <PackCard key={p.id} pack={p} ateliers={ateliers} masterclasses={masterclasses} generalPrice={general.price ?? 0} effectivePrice={packPrice(p)} isSelected={packId === p.id} onApply={() => applyPack(p)} onClear={clearPack} />
                      ))}
                    </div>
                    {matchingPack && (
                      <div style={{ marginTop: 14, marginBottom: 10, padding: '12px 16px', background: 'rgba(201,169,97,0.16)', border: '1px solid rgba(201,169,97,0.5)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', fontSize: 13 }}>
                        <div style={{ flex: 1, minWidth: 200 }}>
                          Votre sélection correspond au <strong>{matchingPack.name}</strong> — économisez{' '}
                          <strong>{fmtMad(autoSum - packPrice(matchingPack))}</strong>.
                        </div>
                        <button type="button" onClick={() => applyPack(matchingPack)} className="mtc-btn mtc-btn-primary" style={{ fontSize: 13 }}>
                          Appliquer le pack →
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {sessions.length > 0 && (
                  <div style={{ marginTop: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, color: 'rgba(15,25,22,0.5)', fontFamily: 'var(--mtc-font-mono)', fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase', margin: '0 0 14px' }}>
                      <span style={{ height: '0.5px', background: 'rgba(15,25,22,0.12)', flex: 1 }} />
                      ou composez à la carte
                      <span style={{ height: '0.5px', background: 'rgba(15,25,22,0.12)', flex: 1 }} />
                    </div>
                    <div style={{ fontFamily: 'var(--mtc-font-mono)', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(15,25,22,0.5)', marginBottom: 10 }}>
                      Ateliers &amp; masterclasses {canRegisterItems ? '' : '· inscription générale requise'}
                    </div>
                    {timeSlots.map((ts) => {
                      const items = grouped.get(ts.id) ?? [];
                      if (items.length === 0) return null;
                      return (
                        <div key={ts.id} style={{ marginBottom: 12 }}>
                          <div style={{ fontFamily: 'var(--mtc-font-mono)', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(15,25,22,0.5)', margin: '0 0 8px' }}>{ts.label}</div>
                          {items.map((s) => (
                            <SessionRow key={s.id} item={s} selected={selectedSet.has(s.id)} disabled={!canRegisterItems} overlapping={overlapsSelection(s)} onToggle={() => toggleSession(s)} speakerNames={(s.conferencierIds ?? []).map(conferencierName).filter(Boolean)} />
                          ))}
                        </div>
                      );
                    })}
                    {(grouped.get(null) ?? []).length > 0 &&
                      (grouped.get(null) ?? []).map((s) => (
                        <SessionRow key={s.id} item={s} selected={selectedSet.has(s.id)} disabled={!canRegisterItems} overlapping={overlapsSelection(s)} onToggle={() => toggleSession(s)} speakerNames={(s.conferencierIds ?? []).map(conferencierName).filter(Boolean)} />
                      ))}
                  </div>
                )}
              </div>

          {/* 3 — Identité */}
          <div className="ev-ins-section">
            <h2 className="ev-ins-section-title">3 · Vos informations</h2>
            {isVerified && memberProfileComplete && !editingIdentity ? (
              <div className="ev-ins-identity-card">
                <div>
                  <div
                    style={{
                      fontSize: 10,
                      fontFamily: 'var(--mtc-font-mono)',
                      letterSpacing: '0.15em',
                      textTransform: 'uppercase',
                      color: 'rgba(15,25,22,0.5)',
                    }}
                  >
                    Inscription en tant que
                  </div>
                  <div className="ev-ins-identity-name">
                    {form.firstName} {form.lastName}
                  </div>
                  <div className="ev-ins-identity-meta">
                    {form.email} · {form.phone}
                  </div>
                </div>
                <button
                  type="button"
                  className="mtc-btn mtc-btn-ghost"
                  onClick={() => setEditingIdentity(true)}
                  style={{ fontSize: 13 }}
                >
                  Modifier
                </button>
              </div>
            ) : (
              <>
                <div className="ev-ins-form-grid">
                  <Field
                    label="Prénom"
                    value={form.firstName}
                    onChange={(v) => setForm((f) => ({ ...f, firstName: v }))}
                    autoComplete="given-name"
                  />
                  <Field
                    label="Nom"
                    value={form.lastName}
                    onChange={(v) => setForm((f) => ({ ...f, lastName: v }))}
                    autoComplete="family-name"
                  />
                  <Field
                    label="Email"
                    value={form.email}
                    onChange={(v) => setForm((f) => ({ ...f, email: v }))}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                  />
                  <Field
                    label="Téléphone"
                    value={form.phone}
                    onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                  />
                  <Field
                    label="Cabinet (optionnel)"
                    value={form.cabinet}
                    onChange={(v) => setForm((f) => ({ ...f, cabinet: v }))}
                  />
                </div>
                {editingIdentity && (
                  <button
                    type="button"
                    onClick={() => {
                      setForm(initialState);
                      setEditingIdentity(false);
                    }}
                    className="mtc-btn mtc-btn-ghost"
                    style={{ alignSelf: 'flex-start', fontSize: 13, marginTop: 14 }}
                  >
                    ← Restaurer mes infos
                  </button>
                )}
                {!isVerified && (
                  <p
                    style={{
                      marginTop: 14,
                      fontSize: 12,
                      color: 'rgba(15,25,22,0.65)',
                      lineHeight: 1.5,
                    }}
                  >
                    <a href="/mon-espace" style={{ color: 'var(--mtc-primary, #0B5D3B)' }}>
                      Vous avez déjà un compte ? Connectez-vous
                    </a>{' '}
                    pour pré-remplir vos informations et retrouver cette inscription dans votre
                    espace membre.
                  </p>
                )}
              </>
            )}

            <div style={{ marginTop: 18 }}>
              <ProofUpload
                evenementId={event.id}
                audience={audience}
                value={audienceProofUrl}
                onChange={setAudienceProofUrl}
              />
            </div>
          </div>

          {/* 4 — Paiement */}
          <div className="ev-ins-section">
            <h2 className="ev-ins-section-title">4 · Mode de paiement</h2>
            <div className="ins-pay-grid">
              {PAYMENT_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`ins-pay-card ${form.paymentPreference === opt.value ? 'is-on' : ''}`}
                >
                  <input
                    type="radio"
                    name="paymentPreference"
                    checked={form.paymentPreference === opt.value}
                    onChange={() => setForm((f) => ({ ...f, paymentPreference: opt.value }))}
                  />
                  <strong>{opt.label}</strong>
                  <span>{opt.hint}</span>
                </label>
              ))}
            </div>
          </div>

          {errorMsg && <div className="ev-ins-error">{errorMsg}</div>}

          <div className="ev-ins-submit-bar">
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontFamily: 'var(--mtc-font-mono)',
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: 'rgba(15,25,22,0.5)',
                }}
              >
                Total à régler
              </div>
              <div className="ev-ins-summary-total-value">{fmtMad(total)}</div>
            </div>
            <button
              type="submit"
              className="mtc-btn mtc-btn-primary mtc-btn-lg"
              disabled={submit.isPending}
            >
              {submit.isPending ? 'Envoi…' : 'Confirmer mon inscription →'}
            </button>
          </div>
            </>
          )}
        </form>

        {/* ── Sidebar: live summary (visible once status is chosen) ─────── */}
        {audience && (
        <aside className="ev-ins-summary">
          <div className="ev-ins-summary-head">Récapitulatif</div>
          {selectedPack && (
            <div
              style={{
                background: 'linear-gradient(135deg, rgba(11,93,59,0.08), rgba(11,93,59,0.02))',
                border: '1px solid rgba(11,93,59,0.25)',
                borderRadius: 8,
                padding: '10px 12px',
                marginBottom: 10,
                fontSize: 12,
              }}
            >
              <div
                style={{
                  fontWeight: 600,
                  color: 'var(--mtc-primary, #0B5D3B)',
                  marginBottom: 2,
                }}
              >
                Pack : {selectedPack.name}
              </div>
              <button
                type="button"
                onClick={clearPack}
                style={{
                  background: 'none',
                  border: 0,
                  color: 'rgba(15,25,22,0.55)',
                  fontSize: 11,
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Retirer le pack
              </button>
            </div>
          )}
          <ul>
            {generalIncluded && (
              <li>
                <span>Inscription générale</span>
                <strong>{general.price > 0 ? fmtMad(general.price) : 'Gratuit'}</strong>
              </li>
            )}
            {selectedAtelierObjs.length === 0 &&
              selectedMasterclassObjs.length === 0 &&
              !generalIncluded && <li className="is-empty">Aucune sélection.</li>}
            {selectedAtelierObjs.map((a) => (
              <li key={a.id}>
                <span>{a.title}</span>
                <strong>{a.price > 0 ? fmtMad(a.price) : 'Gratuit'}</strong>
              </li>
            ))}
            {selectedMasterclassObjs.map((m) => (
              <li key={m.id}>
                <span>{m.title} · Masterclass</span>
                <strong>{m.price > 0 ? fmtMad(m.price) : 'Gratuit'}</strong>
              </li>
            ))}
          </ul>
          <div className="ev-ins-summary-total">
            <span>Total</span>
            <span className="ev-ins-summary-total-value">{fmtMad(total)}</span>
          </div>
          {selectedPack && (
            <p className="ev-ins-summary-note">
              Le prix du pack remplace la somme nominale des items inclus.
            </p>
          )}
        </aside>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PackCard — one card per available pack on the inscription page
// ─────────────────────────────────────────────────────────────────────────
function PackCard({
  pack,
  ateliers,
  masterclasses,
  generalPrice,
  effectivePrice,
  isSelected,
  onApply,
  onClear,
}: {
  pack: EvenementPack;
  ateliers: ReadonlyArray<EvenementAtelier>;
  masterclasses: ReadonlyArray<EvenementMasterclass>;
  generalPrice: number;
  effectivePrice: number;
  isSelected: boolean;
  onApply: () => void;
  onClear: () => void;
}) {
  const autoSum =
    (pack.includes.general ? generalPrice : 0) +
    ateliers
      .filter((a) => pack.includes.atelierIds.includes(a.id))
      .reduce((s, a) => s + (a.price ?? 0), 0) +
    masterclasses
      .filter((m) => pack.includes.masterclassIds.includes(m.id))
      .reduce((s, m) => s + (m.price ?? 0), 0);
  const savings = Math.max(0, autoSum - effectivePrice);
  const hasDiscount = effectivePrice < (pack.price ?? 0);

  const includedItems = [
    pack.includes.general ? 'Inscription générale' : null,
    ...ateliers
      .filter((a) => pack.includes.atelierIds.includes(a.id))
      .map((a) => a.title),
    ...masterclasses
      .filter((m) => pack.includes.masterclassIds.includes(m.id))
      .map((m) => `${m.title} · Masterclass`),
  ].filter(Boolean) as string[];

  return (
    <article
      style={{
        padding: '20px 22px',
        background: isSelected
          ? 'linear-gradient(135deg, rgba(11,93,59,0.08) 0%, rgba(11,93,59,0.02) 100%)'
          : 'white',
        border: `1.5px solid ${isSelected ? 'var(--mtc-primary, #0B5D3B)' : 'rgba(15,25,22,0.1)'}`,
        borderRadius: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        transition: 'border-color 0.18s ease, background 0.18s ease, transform 0.18s ease',
        boxShadow: isSelected
          ? '0 6px 20px rgba(11,93,59,0.18)'
          : '0 2px 8px rgba(15,25,22,0.04)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 10,
              fontFamily: 'var(--mtc-font-mono)',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: 'rgba(15,25,22,0.5)',
              marginBottom: 4,
            }}
          >
            Pack
          </div>
          <h3
            style={{
              fontFamily: 'var(--mtc-font-display)',
              fontSize: 18,
              fontWeight: 500,
              margin: 0,
              color: 'var(--mtc-ink, #0F1916)',
              lineHeight: 1.2,
            }}
          >
            {pack.name}
          </h3>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          {savings > 0 && (
            <div
              style={{
                fontSize: 11,
                color: 'rgba(15,25,22,0.5)',
                textDecoration: 'line-through',
              }}
            >
              {fmtMad(autoSum)}
            </div>
          )}
          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: 'var(--mtc-primary, #0B5D3B)',
              fontFamily: 'var(--mtc-font-display)',
              lineHeight: 1,
            }}
          >
            {fmtMad(effectivePrice)}
          </div>
          {hasDiscount && (
            <div style={{ fontSize: 10, fontWeight: 700, color: '#9E7508', marginTop: 3 }}>
              −{pack.discountPercent}% (votre statut)
            </div>
          )}
        </div>
      </div>
      {pack.description && (
        <p style={{ fontSize: 13, color: 'rgba(15,25,22,0.7)', margin: 0, lineHeight: 1.5 }}>
          {pack.description}
        </p>
      )}
      {includedItems.length > 0 && (
        <ul
          style={{
            margin: 0,
            padding: 0,
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          {includedItems.map((it, i) => (
            <li
              key={i}
              style={{
                fontSize: 12,
                color: 'rgba(15,25,22,0.7)',
                paddingLeft: 18,
                position: 'relative',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  left: 0,
                  color: 'var(--mtc-primary, #0B5D3B)',
                  fontWeight: 700,
                }}
              >
                ✓
              </span>
              {it}
            </li>
          ))}
        </ul>
      )}
      {savings > 0 && !isSelected && (
        <div
          style={{
            background: 'rgba(212,160,23,0.12)',
            color: '#9E7508',
            fontSize: 11,
            fontWeight: 600,
            padding: '4px 10px',
            borderRadius: 999,
            alignSelf: 'flex-start',
          }}
        >
          Économisez {fmtMad(savings)}
        </div>
      )}
      <div style={{ marginTop: 'auto', paddingTop: 4 }}>
        {isSelected ? (
          <button
            type="button"
            onClick={onClear}
            className="mtc-btn mtc-btn-ghost"
            style={{ width: '100%', justifyContent: 'center', fontSize: 13 }}
          >
            ✓ Pack appliqué — retirer
          </button>
        ) : (
          <button
            type="button"
            onClick={onApply}
            className="mtc-btn mtc-btn-primary"
            style={{ width: '100%', justifyContent: 'center', fontSize: 13 }}
          >
            Choisir ce pack →
          </button>
        )}
      </div>
    </article>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// SessionRow — one selectable atelier/masterclass row (à la carte)
// ─────────────────────────────────────────────────────────────────────────
function SessionRow({
  item,
  selected,
  disabled,
  overlapping,
  onToggle,
  speakerNames,
}: {
  item: SessionItem;
  selected: boolean;
  disabled: boolean;
  overlapping: boolean;
  onToggle: () => void;
  speakerNames: string[];
}) {
  const soldOut = item.seats > 0 && item.seatsBooked >= item.seats;
  const placesLeft = item.seats > 0 ? item.seats - item.seatsBooked : null;
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '12px 14px',
        border: `1.5px solid ${selected ? 'var(--mtc-primary, #0B5D3B)' : 'rgba(15,25,22,0.1)'}`,
        borderRadius: 12,
        marginBottom: 8,
        cursor: disabled || soldOut ? 'not-allowed' : 'pointer',
        background: selected ? 'rgba(11,93,59,0.04)' : 'white',
        opacity: disabled || soldOut ? 0.5 : 1,
      }}
    >
      <input
        type="checkbox"
        checked={selected}
        disabled={disabled || soldOut}
        onChange={onToggle}
        style={{ marginTop: 5 }}
      />
      <AfficheThumbnail url={item.afficheUrl} alt={item.title} width={54} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {item.kind === 'masterclass' && (
            <span
              style={{
                fontFamily: 'var(--mtc-font-mono)',
                fontSize: 9,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: '#9E7508',
                border: '0.5px solid #C9A961',
                borderRadius: 4,
                padding: '1px 6px',
              }}
            >
              Masterclass
            </span>
          )}
          <span style={{ fontWeight: 600, color: '#0F1916', fontSize: 14.5 }}>{item.title}</span>
        </div>
        <div style={{ fontSize: 12, color: 'rgba(15,25,22,0.55)', marginTop: 2 }}>
          {sessionTimeLabel(item)}
          {speakerNames.length > 0 ? ` · ${speakerNames.join(', ')}` : ''}
        </div>
        {soldOut ? (
          <span style={{ fontSize: 11, color: '#C8102E', fontWeight: 600 }}>Complet</span>
        ) : overlapping && !selected ? (
          <span style={{ fontSize: 11, color: '#B08D3C', fontWeight: 600 }}>
            Chevauche votre sélection — la remplacera
          </span>
        ) : placesLeft !== null && placesLeft <= 5 ? (
          <span style={{ fontSize: 11, color: 'rgba(15,25,22,0.55)' }}>
            {placesLeft} place{placesLeft > 1 ? 's' : ''} restante{placesLeft > 1 ? 's' : ''}
          </span>
        ) : null}
      </div>
      <div
        style={{
          fontFamily: 'var(--mtc-font-display)',
          fontSize: 15,
          color: 'var(--mtc-primary, #0B5D3B)',
          whiteSpace: 'nowrap',
        }}
      >
        {item.price > 0 ? fmtMad(item.price) : 'Gratuit'}
      </div>
    </label>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ProofUpload — file picker for the audience-status justification (carte
// étudiant, diplôme, carte professionnelle). Uploads on selection to the
// public-writable `evenement-proofs/{evenementId}/` Storage path. ≤ 5 Mo,
// image or PDF only — both enforced client- and Storage-rule-side.
// ─────────────────────────────────────────────────────────────────────────
const PROOF_HINTS: Record<EvenementAudience, string> = {
  all: 'Pièce justifiant votre statut.',
  etudiant: 'Carte d\'étudiant·e ou attestation de scolarité.',
  'jeune-diplome': 'Copie du diplôme ou attestation de fin d\'études.',
  praticien: 'Carte professionnelle ou attestation d\'exercice.',
};

function ProofUpload({
  evenementId,
  audience,
  value,
  onChange,
}: {
  evenementId: string;
  audience: EvenementAudience;
  value: string | null;
  onChange: (url: string | null) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filename, setFilename] = useState<string | null>(null);

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Fichier trop volumineux (maximum 5 Mo).');
      return;
    }
    const isPdf = file.type === 'application/pdf';
    const isImage = file.type.startsWith('image/');
    if (!isPdf && !isImage) {
      setError('Format non supporté — utilisez une image (JPG/PNG/WebP) ou un PDF.');
      return;
    }
    setUploading(true);
    setFilename(file.name);
    try {
      const url = await uploadFile({
        file,
        pathPrefix: `evenement-proofs/${evenementId}`,
        kind: isPdf ? 'pdf' : 'image',
      });
      onChange(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      onChange(null);
    } finally {
      setUploading(false);
    }
  };

  const hint = PROOF_HINTS[audience];

  return (
    <div
      style={{
        marginTop: 18,
        padding: '16px 18px',
        background: value
          ? 'linear-gradient(135deg, rgba(11,93,59,0.06), rgba(11,93,59,0.02))'
          : 'rgba(15,25,22,0.03)',
        border: `1.5px solid ${value ? 'var(--mtc-primary, #0B5D3B)' : 'rgba(15,25,22,0.12)'}`,
        borderRadius: 10,
        transition: 'border-color 0.18s ease, background 0.18s ease',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontFamily: 'var(--mtc-font-mono)',
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          color: 'rgba(15,25,22,0.55)',
          marginBottom: 6,
        }}
      >
        Justificatif de statut
      </div>
      <div style={{ fontSize: 13, color: 'rgba(15,25,22,0.7)', marginBottom: 12 }}>
        {hint} Image ou PDF, 5 Mo max.
      </div>

      <label
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 16px',
          border: '1px solid rgba(15,25,22,0.15)',
          borderRadius: 999,
          background: 'white',
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--mtc-ink, #0F1916)',
          cursor: uploading ? 'wait' : 'pointer',
          transition: 'border-color 0.18s ease, transform 0.18s ease',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--mtc-primary, #0B5D3B)')}
        onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'rgba(15,25,22,0.15)')}
      >
        <input
          type="file"
          accept="image/*,application/pdf"
          onChange={onPick}
          disabled={uploading}
          style={{ display: 'none' }}
        />
        {uploading ? 'Téléversement…' : value ? 'Remplacer le fichier' : 'Choisir un fichier'}
      </label>

      {value && (
        <div
          style={{
            marginTop: 10,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 12px',
            background: 'rgba(11,93,59,0.1)',
            borderRadius: 999,
            fontSize: 12,
            color: 'var(--mtc-primary, #0B5D3B)',
            marginLeft: 10,
          }}
        >
          <span style={{ fontWeight: 700 }}>✓</span>
          {filename ?? 'Justificatif téléversé'}
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'underline' }}
          >
            voir
          </a>
        </div>
      )}

      {error && (
        <div
          style={{
            marginTop: 10,
            fontSize: 12,
            color: '#9F0E25',
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
